import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { SettingsProvider } from "@/hooks/SettingsContext";
import { initAnalytics } from "@/lib/analytics";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AnnouncementBar from "@/components/AnnouncementBar";
import StickyCTA from "@/components/StickyCTA";
import SplashPoster from "@/components/SplashPoster";
import Home from "@/pages/Home";
import ImNew from "@/pages/ImNew";
import About from "@/pages/About";
import Kids from "@/pages/Kids";
import Sermons from "@/pages/Sermons";
import SermonDetail from "@/pages/SermonDetail";
import Events from "@/pages/Events";
import Give from "@/pages/Give";
import Vision from "@/pages/Vision";
import Beliefs from "@/pages/Beliefs";
import Pray from "@/pages/Pray";
import Contact from "@/pages/Contact";
import LocationPage from "@/pages/LocationPage";
import MinistryPage from "@/pages/MinistryPage";
import Resources from "@/pages/Resources";
import ResourcePage from "@/pages/ResourcePage";
import ArticlePage from "@/pages/ArticlePage";
import ChurchesHub from "@/pages/ChurchesHub";
import ChurchLocalPage from "@/pages/ChurchLocalPage";
import { ARTICLES } from "@/data/articles";
import { CHURCH_LOCAL } from "@/data/churchesLocal";
import Privacy from "@/pages/Privacy";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      window.__lenis?.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

function MainShell() {
  const { pathname } = useLocation();
  // Normalize trailing slashes so /about/ never serves duplicate content at a second URL
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return <Navigate to={pathname.replace(/\/+$/, "")} replace />;
  }
  const pad = pathname.startsWith("/admin") ? "" : "pb-16 sm:pb-0";
  return (
    <main id="main-content" className={pad}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/im-new" element={<ImNew />} />
        <Route path="/about" element={<About />} />
        <Route path="/vision" element={<Vision />} />
        <Route path="/beliefs" element={<Beliefs />} />
        <Route path="/kids" element={<Kids />} />
        <Route path="/sermons" element={<Sermons />} />
        <Route path="/sermons/:slug" element={<SermonDetail />} />
        <Route path="/events" element={<Events />} />
        <Route path="/give" element={<Give />} />
        <Route path="/pray" element={<Pray />} />
        <Route path="/prayer" element={<Pray />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/lifeline-youth" element={<MinistryPage slug="lifeline-youth" />} />
        <Route path="/rally-point" element={<MinistryPage slug="rally-point" />} />
        <Route path="/in-home" element={<MinistryPage slug="in-home" />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/:slug" element={<ResourcePage />} />
        <Route path="/churches-in-clay-county-fl" element={<ChurchesHub />} />
        <Route path="/can-god-forgive-me" element={<ArticlePage article={ARTICLES["can-god-forgive-me"]} />} />
        <Route path="/anxiety-and-the-bible" element={<ArticlePage article={ARTICLES["anxiety-and-the-bible"]} />} />
        <Route path="/how-to-pray" element={<ArticlePage article={ARTICLES["how-to-pray"]} />} />
        <Route path="/get-closer-to-god" element={<ArticlePage article={ARTICLES["get-closer-to-god"]} />} />
        <Route path="/who-is-jesus" element={<ArticlePage article={ARTICLES["who-is-jesus"]} />} />
        <Route path="/how-to-follow-jesus" element={<ArticlePage article={ARTICLES["how-to-follow-jesus"]} />} />
        <Route path="/what-does-it-mean-to-be-saved" element={<ArticlePage article={ARTICLES["what-does-it-mean-to-be-saved"]} />} />
        <Route path="/new-christian" element={<ArticlePage article={ARTICLES["new-christian"]} />} />
        <Route path="/what-is-baptism" element={<ArticlePage article={ARTICLES["what-is-baptism"]} />} />
        <Route path="/church-middleburg-fl" element={<ChurchLocalPage data={{ slug: "church-middleburg-fl", ...CHURCH_LOCAL["church-middleburg-fl"] }} />} />
        <Route path="/church-lake-asbury-fl" element={<ChurchLocalPage data={{ slug: "church-lake-asbury-fl", ...CHURCH_LOCAL["church-lake-asbury-fl"] }} />} />
        <Route path="/church-oakleaf-fl" element={<ChurchLocalPage data={{ slug: "church-oakleaf-fl", ...CHURCH_LOCAL["church-oakleaf-fl"] }} />} />
        <Route path="/church-orange-park-fl" element={<ChurchLocalPage data={{ slug: "church-orange-park-fl", ...CHURCH_LOCAL["church-orange-park-fl"] }} />} />
        <Route path="/church-fleming-island-fl" element={<ChurchLocalPage data={{ slug: "church-fleming-island-fl", ...CHURCH_LOCAL["church-fleming-island-fl"] }} />} />
        <Route path="/church-green-cove-springs-fl" element={<ChurchLocalPage data={{ slug: "church-green-cove-springs-fl", ...CHURCH_LOCAL["church-green-cove-springs-fl"] }} />} />
        <Route path="/lake-asbury" element={<LocationPage slug="lake-asbury" />} />
        <Route path="/middleburg" element={<LocationPage slug="middleburg" />} />
        <Route path="/oakleaf" element={<LocationPage slug="oakleaf" />} />
        <Route path="/fleming-island" element={<LocationPage slug="fleming-island" />} />
        <Route path="/orange-park" element={<LocationPage slug="orange-park" />} />
        <Route path="/green-cove-springs" element={<LocationPage slug="green-cove-springs" />} />
        <Route path="/lakeside" element={<LocationPage slug="lakeside" />} />
        <Route path="/keystone-heights" element={<LocationPage slug="keystone-heights" />} />
        <Route path="/doctors-inlet" element={<LocationPage slug="doctors-inlet" />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </main>
  );
}

function App() {
  useEffect(() => {
    initAnalytics();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.1 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <SettingsProvider>
      <BrowserRouter>
        <ScrollManager />
        <AnnouncementBar />
        <Navbar />
        <MainShell />
        <Footer />
        <StickyCTA />
        <SplashPoster />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </SettingsProvider>
  );
}

export default App;
